import { isEqual } from "lodash";

import type { Signal, SignalType } from "app/pages/sales-intelligence/domain/signals/types/signal";
import { signalTypeMap } from "app/pages/sales-intelligence/domain/signals/types/signal";
import type { SignalsDTO } from "app/pages/sales-intelligence/domain/signals/types/signal-collection";
import { isDefinedFilterDefault } from "app/pages/sales-intelligence/modules/advanced-search-filtering/utils/collectFilterDefaults";
import type { SearchFilterInstance } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/searchFilterInstance";
import type { ILeadsSearchFiltersDto } from "app/pages/sales-intelligence/modules/leads-search-filters/types/leadsSearchFilters.dto";
import {
    isKeyedFilterCategory,
    KEYED_FILTER_CATEGORIES,
    type KeyedFilterCategory,
} from "app/pages/sales-intelligence/modules/leads-search-filters/utils/keyedFilterCategories";

import type { TeamFilters } from "../types/teams.types";

type FiltersDto = Partial<ILeadsSearchFiltersDto>;
type KeyedFilters = Record<string, unknown>;

export type TerritoryOverlayFilterInstance = Pick<SearchFilterInstance, "key" | "apiCategory"> & {
    readonly filter: Pick<SearchFilterInstance["filter"], "getEmptyValue">;
};

const buildDefaultLookup = (
    filterInstances: readonly TerritoryOverlayFilterInstance[] | undefined,
): ReadonlyMap<string, unknown> => {
    const lookup = new Map<string, unknown>();

    if (!filterInstances?.length) {
        return lookup;
    }

    for (const { key, apiCategory, filter } of filterInstances) {
        if (!apiCategory || !isKeyedFilterCategory(apiCategory)) {
            continue;
        }

        const emptyValue = filter.getEmptyValue();
        if (isDefinedFilterDefault(emptyValue)) {
            lookup.set(`${apiCategory}:${String(key)}`, emptyValue);
        }
    }

    return lookup;
};

/** Remove the team's keys from current, then layer the next team's keys on top. User keys remain. */
const stripAndMerge = (
    current: KeyedFilters | undefined,
    prevTeam: KeyedFilters | undefined,
    nextTeam: KeyedFilters | undefined,
    category: KeyedFilterCategory,
    defaultLookup: ReadonlyMap<string, unknown>,
): KeyedFilters => {
    const stripped = { ...(current ?? {}) };

    if (prevTeam) {
        for (const key of Object.keys(prevTeam)) {
            if (!(key in stripped)) {
                continue;
            }

            const currentValue = stripped[key];
            delete stripped[key];

            const defaultValue = defaultLookup.get(`${category}:${key}`);
            if (defaultValue !== undefined && isEqual(currentValue, defaultValue)) {
                stripped[key] = defaultValue;
            }
        }
    }

    return { ...stripped, ...(nextTeam ?? {}) };
};

// Whole-unit overlay for the array-shaped CRM filters: the next team's value wins; otherwise the previous
// team's value is stripped (CRM is team-or-user-exclusive — a team switch replaces any CRM selection).
const overlayWholeUnit = <T>(
    current: T | undefined,
    prevTeam: T | undefined,
    nextTeam: T | undefined,
): T | undefined => {
    if (nextTeam !== undefined) {
        return nextTeam;
    }

    return prevTeam !== undefined ? undefined : current;
};

const signalIds = (signals: readonly Signal[] | undefined): ReadonlySet<string> =>
    new Set((signals ?? []).map((signal) => signal.id));

const hasAnySignals = (dto: SignalsDTO | undefined): boolean =>
    !!dto && Object.values(dto).some((signals) => (signals as readonly Signal[] | undefined)?.length);

/**
 * Per signal type: keep the user's signals (those not owned by the previous team), then layer the
 * next team's signals on top (de-duplicated by id). Mirrors the keyed-filter rule at the id level.
 */
const overlaySignals = (
    current: SignalsDTO | undefined,
    prevTeam: SignalsDTO | undefined,
    nextTeam: SignalsDTO | undefined,
): SignalsDTO => {
    return Object.values(signalTypeMap).reduce<Record<SignalType, readonly Signal[]>>(
        (acc, type) => {
            const prevTeamIds = signalIds(prevTeam?.[type]);
            const userSignals = (current?.[type] ?? []).filter((signal) => !prevTeamIds.has(signal.id));

            const nextSignals = nextTeam?.[type] ?? [];
            const nextIds = signalIds(nextSignals);

            acc[type] = [...userSignals.filter((signal) => !nextIds.has(signal.id)), ...nextSignals];
            return acc;
        },
        {} as Record<SignalType, readonly Signal[]>,
    );
};

/**
 * The single rule behind every territory action (default-on / toggle / switch team): strip the
 * previously-applied team's filters off `current`, then merge the next team's filters on top —
 * never touching filters the user added themselves.
 *
 * `nextTeam` is `undefined` when the toggle is OFF (strip only).
 *
 * When `filterInstances` is provided, stripped keys whose pre-overlay value matched the model's
 * `getEmptyValue` are re-seeded (e.g. after Clear All).
 *
 * Signals are part of the returned DTO so the single `setLeadsFilterValue` dispatch hydrates the
 * signals slice too (it listens to that action). When the team's signals are removed we still emit
 * a defined `signalsFilters`, because the signals reducer ignores an `undefined` payload.
 */
export const applyTeamFilterOverlay = (
    current: FiltersDto,
    prevTeam: TeamFilters | undefined,
    nextTeam: TeamFilters | undefined,
    filterInstances?: readonly TerritoryOverlayFilterInstance[],
): FiltersDto => {
    const defaultLookup = buildDefaultLookup(filterInstances);
    const result: Record<string, unknown> = {};

    for (const category of KEYED_FILTER_CATEGORIES) {
        const merged = stripAndMerge(
            current[category] as KeyedFilters | undefined,
            prevTeam?.[category] as KeyedFilters | undefined,
            nextTeam?.[category] as KeyedFilters | undefined,
            category,
            defaultLookup,
        );

        if (Object.keys(merged).length > 0) {
            result[category] = merged;
        }
    }

    const crmFilters = overlayWholeUnit(
        current.salesforceCustomFilters,
        prevTeam?.salesforceCustomFilters,
        nextTeam?.salesforceCustomFilters,
    );

    if (crmFilters?.length) {
        result.salesforceCustomFilters = crmFilters;
    }

    // Only touch signals when there's something to apply or clear, so we don't needlessly reset them.
    const signalsInvolved =
        hasAnySignals(current.signalsFilters) ||
        hasAnySignals(prevTeam?.signalsFilters) ||
        hasAnySignals(nextTeam?.signalsFilters);

    if (signalsInvolved) {
        result.signalsFilters = overlaySignals(
            current.signalsFilters,
            prevTeam?.signalsFilters,
            nextTeam?.signalsFilters,
        );
    }

    return result as FiltersDto;
};
